import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ByzMuiIconButton } from "../buttons/ByzMuiIconButton";
import ByzMuiButton from "../buttons/ByzMuiButton";
import ByzMuiTextField from "../inputs/ByzMuiTextField";
import ByzOkeyButton from "../buttons/ByzOkeyButton";
import { useTranslate } from "src/locales";

/**
 * ByzDeleteModal - Genel amaçlı onay modal'ı
 * İptal/Silme gibi kritik işlemler için açıklama gerektirebilen modal component
 */

export interface ByzDeleteModalProps {
  /** Modal'ın açık/kapalı durumu */
  open: boolean;
  /** Modal'ı kapatma fonksiyonu */
  onClose: () => void;
  /** Onay butonuna tıklandığında çalışacak fonksiyon */
  onConfirm: (explanation?: string) => void;
  /** Modal başlığı */
  title?: string;
  /** Modal içerik metni */
  content?: string;
  /** Açıklama alanı label */
  explanationLabel?: string;
  /** İptal butonu metni */
  cancelText?: string;
  /** Loading durumu */
  loading?: boolean;
  /** Modal genişliği */
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

const ByzDeleteModal: React.FC<ByzDeleteModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  content,
  explanationLabel,
  cancelText,
  loading = false,
  maxWidth = "xs",
}) => {
  const { t } = useTranslate();
  const [explanation, setExplanation] = useState("");

  // Modal açıldığında açıklama alanını temizle
  useEffect(() => {
    if (open) {
      setExplanation("");
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm(explanation);
  };

  const handleClose = () => {
    setExplanation("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          borderRadius: "8px",
        },
      }}
    >
      {/* Header - Title ve Close Button */}
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: { xs: "1rem", sm: "1.1rem" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          px: 3,
          pt: 2,
        }}
      >
        {title || t("delete-confirm.title")}
        <ByzMuiIconButton onClick={handleClose} disabled={loading} sx={{ padding: 0 }}>
          <CloseIcon sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
        </ByzMuiIconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ px: 3, pb: 2 }}>
        {content && (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ marginBottom: "16px" }}
          >
            {content}
          </Typography>
        )}

        {/* Açıklama Alanı */}
        <Box sx={{ mt: 2 }}>
          <ByzMuiTextField
            fullWidth
            variant="outlined"
            label={explanationLabel || t("general.messages.delete-confirm.explanation-placeholder")}
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            disabled={loading}
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <ByzMuiButton
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={loading}
        >
          {cancelText || t("buttons.cancel")}
        </ByzMuiButton>

        <ByzOkeyButton
          onClick={handleConfirm}
          disabled={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

export default ByzDeleteModal;

